import React, { useContext, useEffect, useId } from 'react'

import type { ModalComponent } from './ModalContext'
import { ModalContext, useUpdateModalData } from './ModalContext'
import ModalRender from './ModalRender'

interface IModalMountProps<P, R> {
  component: ModalComponent<P, R>
  userMount?: boolean
  stopPropagation?: boolean
}

const ModalMount = <P, R>({
  component,
  userMount = true,
  stopPropagation,
}: IModalMountProps<P, R>) => {
  const id = useId()
  const updateModal = useUpdateModalData(component)
  const { modals, modalPortal } = useContext(ModalContext)
  const modalData = modals.get(component as ModalComponent<unknown, unknown>)
  const onClose = (result?: R) => {
    updateModal((data) => {
      if (data.deferred) {
        data.deferred.resolve(result)
      }

      return { ...data, open: false }
    })
  }
  useEffect(() => {
    if (userMount) {
      updateModal((data) => {
        if (data.mounted && data.mounted !== id) {
          throw new Error(
            `Modal already mounted. Mounted id: ${data.mounted}. Current modal id: ${id}`,
          )
        }

        if (!data.mounted) {
          return { ...data, mounted: id }
        }

        return data
      })
    }

    return () => {
      if (userMount) {
        updateModal((data) => {
          if (data.mounted === id) {
            return { ...data, mounted: undefined }
          }

          return data
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component])

  const currentModalIndex = [...modals.values()]
    .filter((modal) => modal.open)
    .map((modal) => modal.index)
    .sort((a, b) => b - a)[0]

  const getContainer = () => modalPortal.current
  useEffect(() => {
    return () => {
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ModalRender
      component={component}
      currentModalIndex={currentModalIndex}
      getContainer={getContainer}
      index={modalData?.index}
      onClose={onClose}
      open={modalData?.open || false}
      params={modalData?.params as P}
      stopPropagation={stopPropagation}
    />
  )
}

export default React.memo(ModalMount) as typeof ModalMount
